import mixpanel, { Mixpanel } from 'mixpanel-browser';
import dataModel from '../../models/data';
import { qsConfigs } from '../../lib/helpers';
import shortClientId from '../../lib/shortClientId';
import * as Sentry from '@sentry/react';

const emailRegex = /email[^&]*/g;

export class Base {
  protected instance?: Mixpanel;
  protected default_properties: {
    appUID?: string;
    sdkVersion?: string;
    sdkPlatform?: string;
  } = {};

  public init(): Promise<void> {
    const {
      analytics: { token, debug }
    } = dataModel;

    return new Promise((resolve) => {
      if (!token) return resolve();

      mixpanel.init(token, {
        debug,
        loaded: (mixpanelInstance) => {
          this.instance = mixpanelInstance;

          const configs = qsConfigs();

          this.default_properties = {
            appUID:
              shortClientId(
                dataModel.cobrandClientApp.uid || dataModel.clientApp.uid
              ) || undefined,
            sdkVersion: configs.sdkVersion as string,
            sdkPlatform: configs.sdkPlatform as string
          };

          resolve();
        }
      });

      if (dataModel.env === 'development') return resolve();

      Sentry.init({
        dsn: 'https://f5264f0afd0b4655a866cd0d00d824f0@o23392.ingest.us.sentry.io/1544024',
        environment: dataModel.env,
        release: process.env.REACT_APP_VERSION,
        beforeBreadcrumb: (breadcrumb) => {
          if (breadcrumb.data?.from && breadcrumb.data.from.includes('email')) {
            breadcrumb = {
              ...breadcrumb,
              data: {
                ...breadcrumb.data,
                from: breadcrumb.data.from.replace(
                  emailRegex,
                  'email[REDACTED]'
                )
              }
            };
          }
          if (breadcrumb.data?.to && breadcrumb.data.to.includes('email')) {
            breadcrumb = {
              ...breadcrumb,
              data: {
                ...breadcrumb.data,
                to: breadcrumb.data.to.replace(emailRegex, 'email[REDACTED]')
              }
            };
          }
          if (breadcrumb.data?.url && breadcrumb.data.url.includes('email')) {
            breadcrumb = {
              ...breadcrumb,
              data: {
                ...breadcrumb.data,
                url: breadcrumb.data.url.replace(emailRegex, 'email[REDACTED]')
              }
            };
          }

          return breadcrumb;
        },
        beforeSend: (event) => {
          if (event?.request?.url && event.request.url.includes('email')) {
            event = {
              ...event,
              request: {
                ...event.request,
                url: event.request.url.replace(emailRegex, 'email[REDACTED]')
              }
            };
          }

          if (event?.exception?.values) {
            event = {
              ...event,
              exception: {
                ...event.exception,
                values: event.exception?.values.map((value) => {
                  if (value?.stacktrace?.frames) {
                    value = {
                      ...value,
                      stacktrace: {
                        ...value.stacktrace,
                        frames: value.stacktrace.frames.map((frame) => {
                          if (
                            frame.abs_path &&
                            frame.abs_path.includes('email')
                          ) {
                            frame = {
                              ...frame,
                              abs_path: frame.abs_path.replace(
                                emailRegex,
                                'email[REDACTED]'
                              )
                            };
                          }

                          if (
                            frame.filename &&
                            frame.filename.includes('email')
                          ) {
                            frame = {
                              ...frame,
                              filename: frame.filename.replace(
                                emailRegex,
                                'email[REDACTED]'
                              )
                            };
                          }

                          return frame;
                        })
                      }
                    };
                  }

                  return value;
                })
              }
            };
          }

          return event;
        }
      });
    });
  }

  get distinctId() {
    return this.instance?.get_distinct_id();
  }

  public reset() {
    this.instance?.reset();
  }

  public identify() {
    if (dataModel.uid) {
      this.instance?.identify(dataModel.uid);
    }
  }
}
